import { hubPageBg } from './accountHubStyles';
import InfoIconButton from './InfoIconButton';
import { FC, JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import ProxyBanner from './ProxyBanner';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import {
  selectElevatedAuthIsValid,
  selectPersonalInfoFor,
  selectPersonalInfoLoadingFor,
  selectPersonalInfoErrorFor,
  selectRevealedSsnFor,
  setElevatedAuth,
  setPersonalInfo,
  setPersonalInfoLoading,
  setPersonalInfoError,
  setRevealedSsn,
  clearRevealedSsn,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import {
  getUserProfile,
  patchUserProfile,
  revealSsn as revealSsnApi,
  UserProfilePatch,
} from 'src/services/PersonalInfo.service';
import VerifyCodeModal from './VerifyCodeModal';
import {
  GENDER_LABELS,
  GENDER_OPTIONS,
  DISABILITY_LABELS,
  DISABILITY_OPTIONS,
  DISABILITY_TYPE_LABELS,
  DISABILITY_TYPE_OPTIONS,
  RACE_LABELS,
  RACE_OPTIONS,
  ASIAN_SUB_LABELS,
  ASIAN_SUB_OPTIONS,
  PACIFIC_ISLANDER_SUB_LABELS,
  PACIFIC_ISLANDER_SUB_OPTIONS,
  ETHNICITY_LABELS,
  ETHNICITY_OPTIONS,
  CITIZENSHIP_LABELS,
  CITIZENSHIP_OPTIONS,
} from './personalInfoChoices';

type EditableField =
  | 'first_name'
  | 'middle_name'
  | 'last_name'
  | 'date_of_birth'
  | 'ssn'
  | 'gender'
  | 'disability_status'
  | 'disability_types'
  | 'race_ethnicity'
  | 'asian_sub'
  | 'pacific_islander_sub'
  | 'ethnicity'
  | 'citizenship_status';

type PendingAction =
  | { type: 'edit'; field: EditableField }
  | { type: 'reveal' };

function formatDob(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  const [year, month, day] = iso.split('-');
  return `${month}/${day}/${year}`;
}

function getErrorCode(err: unknown): string | null {
  return (
    (err as { response?: { data?: { code?: string } } })?.response?.data
      ?.code ?? null
  );
}

/**
 * Auto-formats a raw SSN input into XXX-XX-XXXX.
 * Mirrors the normalizeSSN logic used in QISSN.jsx / QuestionnaireSSN.jsx.
 */
function normalizeSSN(value: string, previousValue: string): string {
  if (!value || (previousValue && value.length < previousValue.length)) {
    return value;
  }
  const digits = value.replace(/[^\d]/g, '');
  const len = digits.length;
  if (
    (value.endsWith('-') && (value.length === 4 || value.length === 7)) ||
    value.endsWith('--')
  ) {
    if (value.endsWith('--')) {
      return value.slice(0, value.length - 1);
    }
    return value;
  }
  if (!previousValue || value.length > previousValue.length) {
    if (len < 4) {
      return digits;
    }
    if (len < 6) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}`;
    }
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
  }
  return value;
}

/** Returns true when the value is a fully-formed XXX-XX-XXXX SSN or empty. */
function isValidSSN(value: string): boolean {
  if (!value) {
    return true;
  }
  return /^\d{3}-\d{2}-\d{4}$/.test(value);
}

// Valid race values derived from RACE_LABELS — used to strip legacy entries
// (e.g. historic hispanic_or_latino) that no longer belong in race_ethnicity.
const VALID_RACE_VALUES = new Set(Object.keys(RACE_LABELS));

const PersonalInfoPage: FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const targetUserId = Number(userId);
  const dispatch = useDispatch();

  const elevatedAuthState = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.accountHub.elevatedAuth
  );
  const isElevated = useSelector(selectElevatedAuthIsValid);
  const profile = useSelector(selectPersonalInfoFor(targetUserId));
  const loading = useSelector(selectPersonalInfoLoadingFor(targetUserId));
  const fetchError = useSelector(selectPersonalInfoErrorFor(targetUserId));
  const revealedSsn = useSelector(selectRevealedSsnFor(targetUserId));

  const backRoute = `/${RENTER_PATHS.HOME}/${RENTER_PATHS.ACCOUNT_HUB}/${userId}`;

  // Which field is currently in edit mode
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  // Draft value while editing
  const [draftValue, setDraftValue] = useState<string | string[]>('');
  // SSN draft show/hide toggle (separate from the reveal toggle)
  const [showSsnDraft, setShowSsnDraft] = useState(false);
  // Loading state while fetching the full SSN for edit pre-population
  const [ssnEditLoading, setSsnEditLoading] = useState(false);
  // Inline SSN validation error
  const [ssnError, setSsnError] = useState<string | null>(null);
  // Inline save error
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Access denied (proxy.not_authorized on GET)
  const [accessDenied, setAccessDenied] = useState(false);

  // VerifyCodeModal state: open when we need elevated auth before proceeding
  const [verifyOpen, setVerifyOpen] = useState(false);
  // What to do after elevation succeeds
  const pendingActionRef = useRef<PendingAction | null>(null);

  const fetchSsnForEdit = useCallback(async (): Promise<void> => {
    setSsnEditLoading(true);
    try {
      const res = await revealSsnApi(targetUserId);
      dispatch(setRevealedSsn({ userId: targetUserId, ssn: res.ssn }));
      setDraftValue(res.ssn);
    } catch {
      setDraftValue('');
    } finally {
      setSsnEditLoading(false);
    }
    setEditingField('ssn');
    setSaveError(null);
    setSsnError(null);
    setShowSsnDraft(false);
  }, [dispatch, targetUserId]);

  // Load profile on mount
  const loadProfile = useCallback(async (): Promise<void> => {
    dispatch(setPersonalInfoLoading({ userId: targetUserId, loading: true }));
    try {
      const data = await getUserProfile(targetUserId);
      dispatch(setPersonalInfo({ userId: targetUserId, data }));
    } catch (err: unknown) {
      const code = getErrorCode(err);
      if (code === 'proxy.not_authorized') {
        setAccessDenied(true);
        dispatch(setPersonalInfoError({ userId: targetUserId, error: null }));
      } else {
        dispatch(
          setPersonalInfoError({
            userId: targetUserId,
            error: 'Failed to load personal information.',
          })
        );
      }
    }
  }, [dispatch, targetUserId]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // Clear revealed SSN on unmount and on tab hide
  useEffect(() => {
    const handleVisibility = (): void => {
      if (document.visibilityState === 'hidden') {
        dispatch(clearRevealedSsn(targetUserId));
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      dispatch(clearRevealedSsn(targetUserId));
    };
  }, [dispatch, targetUserId]);

  // Open edit for a field — gate by elevated auth
  const handleEditClick = useCallback(
    (field: EditableField): void => {
      if (!isElevated) {
        pendingActionRef.current = { type: 'edit', field };
        setVerifyOpen(true);
        return;
      }

      // SSN: fetch the full value first, then open edit pre-populated
      if (field === 'ssn') {
        void fetchSsnForEdit();
        return;
      }

      let initial: string | string[] = '';
      if (profile) {
        if (field === 'race_ethnicity') {
          initial = (profile.race_ethnicity ?? [])[0] ?? '';
        } else if (field === 'disability_types') {
          initial = profile.disability_types ?? [];
        } else if (field === 'asian_sub' || field === 'pacific_islander_sub') {
          initial = ((profile[field] as string[] | undefined) ?? [])[0] ?? '';
        } else if (field === 'date_of_birth') {
          initial = profile.date_of_birth ?? '';
        } else {
          initial = (profile[field as keyof typeof profile] as string) ?? '';
        }
      }
      setDraftValue(initial);
      setEditingField(field);
      setSaveError(null);
      setSsnError(null);
      setShowSsnDraft(false);
    },
    [dispatch, fetchSsnForEdit, isElevated, profile, targetUserId]
  );

  // Save the drafted edit
  const handleSave = useCallback(async (): Promise<void> => {
    if (!editingField) {
      return;
    }

    // SSN validation before hitting the network
    if (editingField === 'ssn') {
      const val = draftValue as string;
      if (val && !isValidSSN(val)) {
        setSsnError('Enter a valid SSN in the format XXX-XX-XXXX.');
        return;
      }
      setSsnError(null);
    }

    setSaving(true);
    setSaveError(null);
    const buildPatch = (
      editingField: EditableField,
      draftValue: string | string[]
    ): UserProfilePatch => {
      if (editingField === 'disability_types') {
        return { [editingField]: draftValue as string[] };
      }
      if (
        editingField === 'asian_sub' ||
        editingField === 'pacific_islander_sub'
      ) {
        const val = draftValue as string;
        return { [editingField]: val ? [val] : [] };
      }
      if (editingField === 'race_ethnicity') {
        const val = draftValue as string;
        return {
          race_ethnicity: val ? [val] : [],
          ...(!val || val !== 'asian' ? { asian_sub: [] } : {}),
          ...(!val || val !== 'native_hawaiian_pacific_islander'
            ? { pacific_islander_sub: [] }
            : {}),
        };
      }
      if (editingField === 'disability_status' && draftValue !== 'yes') {
        return {
          disability_status: draftValue as string,
          disability_types: [],
        };
      }
      return { [editingField]: draftValue as string };
    };

    const patch: UserProfilePatch = buildPatch(editingField, draftValue);

    try {
      const updated = await patchUserProfile(targetUserId, patch);
      dispatch(setPersonalInfo({ userId: targetUserId, data: updated }));
      if (editingField === 'ssn') {
        dispatch(clearRevealedSsn(targetUserId));
      }
      setEditingField(null);
    } catch (err: unknown) {
      const code = getErrorCode(err);
      if (code === 'elevated_auth_required') {
        // Session expired mid-edit: re-elevate then retry
        pendingActionRef.current = { type: 'edit', field: editingField };
        setVerifyOpen(true);
      } else {
        setSaveError('Failed to save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }, [dispatch, draftValue, editingField, targetUserId]);

  const handleCancel = useCallback((): void => {
    if (editingField === 'ssn') {
      dispatch(clearRevealedSsn(targetUserId));
    }
    setEditingField(null);
    setSaveError(null);
    setSsnError(null);
    setShowSsnDraft(false);
  }, [dispatch, editingField, targetUserId]);

  // SSN reveal toggle
  const handleRevealToggle = useCallback((): void => {
    if (revealedSsn !== null) {
      dispatch(clearRevealedSsn(targetUserId));
      return;
    }
    if (!isElevated) {
      pendingActionRef.current = { type: 'reveal' };
      setVerifyOpen(true);
      return;
    }
    const revealSsn = async (): Promise<void> => {
      try {
        const res = await revealSsnApi(targetUserId);
        dispatch(setRevealedSsn({ userId: targetUserId, ssn: res.ssn }));
      } catch {
        // Silently ignore; user can retry
      }
    };
    void revealSsn();
  }, [dispatch, isElevated, revealedSsn, targetUserId]);

  // Called by VerifyCodeModal on success
  const handleElevationSuccess = useCallback(
    (expiresAt: string, method: 'otp' | 'totp'): void => {
      dispatch(
        setElevatedAuth({
          verified: true,
          expires_at: expiresAt,
          verified_at: new Date().toISOString(),
          method,
        })
      );
      setVerifyOpen(false);

      const pending = pendingActionRef.current;
      pendingActionRef.current = null;

      if (!pending) {
        return;
      }
      if (pending.type === 'reveal') {
        const revealSsnAfterElevation = async (): Promise<void> => {
          try {
            const res = await revealSsnApi(targetUserId);
            dispatch(setRevealedSsn({ userId: targetUserId, ssn: res.ssn }));
          } catch {
            // Silently ignore
          }
        };
        void revealSsnAfterElevation();
      } else {
        // Open the edit surface after elevation
        if (pending.field === 'ssn') {
          void fetchSsnForEdit();
          return;
        }

        let initial: string | string[] = '';
        if (profile) {
          if (pending.field === 'race_ethnicity') {
            initial = (profile.race_ethnicity ?? [])[0] ?? '';
          } else if (
            pending.field === 'asian_sub' ||
            pending.field === 'pacific_islander_sub'
          ) {
            initial =
              ((profile[pending.field] as string[] | undefined) ?? [])[0] ?? '';
          } else if (pending.field === 'disability_types') {
            initial = profile.disability_types ?? [];
          } else if (pending.field === 'date_of_birth') {
            initial = profile.date_of_birth ?? '';
          } else {
            initial =
              (profile[pending.field as keyof typeof profile] as string) ?? '';
          }
        }
        setDraftValue(initial);
        setEditingField(pending.field);
        setSaveError(null);
        setSsnError(null);
        setShowSsnDraft(false);
      }
    },
    [dispatch, fetchSsnForEdit, profile, targetUserId]
  );

  // Render helpers
  const renderEditIcon = (field: EditableField): JSX.Element => {
    const isLoading = field === 'ssn' && ssnEditLoading;
    return (
      <IconButton
        size="small"
        aria-label={`Edit ${field.replace(/_/g, ' ')}`}
        onClick={() => handleEditClick(field)}
        disabled={isLoading}
        sx={{ ml: 1 }}
      >
        {isLoading ? (
          <CircularProgress size={16} />
        ) : (
          <EditIcon fontSize="small" />
        )}
      </IconButton>
    );
  };

  const renderSaveCancel = (): JSX.Element => {
    const ssnInvalid =
      editingField === 'ssn' &&
      (draftValue as string).length > 0 &&
      !isValidSSN(draftValue as string);

    return (
      <Stack direction="row" gap={1} mt={1}>
        <Button
          size="small"
          variant="contained"
          onClick={() => void handleSave()}
          disabled={saving || ssnInvalid}
          startIcon={saving ? <CircularProgress size={12} /> : null}
          sx={{ borderRadius: '8px', fontSize: 12 }}
        >
          <span>{saving ? 'Saving…' : 'Save'}</span>
        </Button>
        <Button
          size="small"
          onClick={handleCancel}
          disabled={saving}
          sx={{ borderRadius: '8px', fontSize: 12 }}
        >
          Cancel
        </Button>
      </Stack>
    );
  };

  const renderFieldRow = (
    label: string,
    field: EditableField,
    displayValue: string | JSX.Element,
    editControl: JSX.Element,
    infoText?: string
  ): JSX.Element => {
    const isEditing = editingField === field;
    return (
      <Box key={field}>
        <Stack
          direction="row"
          alignItems={isEditing ? 'flex-start' : 'center'}
          justifyContent="space-between"
          sx={{ px: 2, py: 1.75 }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            sx={{ flex: 1, mr: 2 }}
          >
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
              {label}
            </Typography>
            {infoText && <InfoIconButton label={label} info={infoText} />}
          </Stack>
          {isEditing ? (
            <Stack alignItems="flex-end" flex={1} sx={{ minWidth: 0 }}>
              {editControl}
              {saveError && (
                <Alert severity="error" sx={{ mt: 1, fontSize: 12, py: 0 }}>
                  {saveError}
                </Alert>
              )}
              {renderSaveCancel()}
            </Stack>
          ) : (
            <Stack direction="row" alignItems="center">
              <Box
                component="span"
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: 'text.primary',
                  textAlign: 'right',
                }}
              >
                {displayValue}
              </Box>
              {renderEditIcon(field)}
            </Stack>
          )}
        </Stack>
      </Box>
    );
  };

  // --- Render ---
  if (loading && !profile) {
    return (
      <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
        <PageHeader
          title="Personal Information"
          icon={<PersonIcon />}
          backRoute={backRoute}
          subtitle="Your demographic and personal details"
        />
        <Stack gap={1} sx={{ p: 3 }}>
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={52}
              sx={{ borderRadius: '8px' }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
        <PageHeader
          title="Personal Information"
          icon={<PersonIcon />}
          backRoute={backRoute}
          subtitle="Your demographic and personal details"
        />
        <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
          <Typography
            sx={{ fontSize: 14, color: 'text.secondary', textAlign: 'center' }}
          >
            You don&apos;t have access to this person&apos;s information.
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
        <PageHeader
          title="Personal Information"
          icon={<PersonIcon />}
          backRoute={backRoute}
          subtitle="Your demographic and personal details"
        />
        <Alert severity="error" sx={{ m: 3 }}>
          {fetchError}
        </Alert>
      </Box>
    );
  }

  const ssnDisplay = (): JSX.Element => {
    if (revealedSsn !== null) {
      return (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            {revealedSsn}
          </Typography>
          <IconButton
            size="small"
            onClick={handleRevealToggle}
            aria-label="Hide SSN"
          >
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    }
    const masked = profile?.ssn_last_four
      ? `•••-••-${profile.ssn_last_four}`
      : '—';
    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{masked}</Typography>
        <IconButton
          size="small"
          onClick={handleRevealToggle}
          aria-label="Reveal SSN"
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Stack>
    );
  };

  const ssnEditControl = (): JSX.Element => (
    <Stack gap={0.5} sx={{ width: 200 }}>
      <TextField
        size="small"
        value={draftValue as string}
        type={showSsnDraft ? 'text' : 'password'}
        placeholder="XXX-XX-XXXX"
        error={ssnError !== null}
        helperText={ssnError ?? undefined}
        onChange={(e) => {
          const normalized = normalizeSSN(e.target.value, draftValue as string);
          setDraftValue(normalized);
          if (ssnError && isValidSSN(normalized)) {
            setSsnError(null);
          }
        }}
        inputProps={{ 'data-testid': 'edit-ssn', 'data-dd-privacy': 'mask' }}
        slotProps={{
          input: {
            autoComplete: 'one-time-code',
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={() => setShowSsnDraft((s) => !s)}
                  aria-label={
                    showSsnDraft ? 'Hide SSN draft' : 'Show SSN draft'
                  }
                >
                  {showSsnDraft ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        sx={{ width: 200 }}
      />
    </Stack>
  );

  const disabilityTypesEditControl = (): JSX.Element => (
    <FormGroup>
      {DISABILITY_TYPE_OPTIONS.map((opt) => (
        <FormControlLabel
          key={opt.value}
          control={
            <Checkbox
              size="small"
              checked={(draftValue as string[]).includes(opt.value)}
              onChange={(e) => {
                const cur = draftValue as string[];
                if (e.target.checked) {
                  setDraftValue([...cur, opt.value]);
                } else {
                  setDraftValue(cur.filter((v) => v !== opt.value));
                }
              }}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>{opt.label}</Typography>}
        />
      ))}
    </FormGroup>
  );

  const selectEditControl = (
    options: { value: string; label: string }[]
  ): JSX.Element => (
    <Select
      size="small"
      value={draftValue as string}
      onChange={(e: SelectChangeEvent<string>) => setDraftValue(e.target.value)}
      sx={{ minWidth: 180, maxWidth: '100%', width: '100%' }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );

  const textEditControl = (
    fieldKey: string,
    type: 'text' | 'date' = 'text'
  ): JSX.Element => (
    <TextField
      size="small"
      type={type}
      value={draftValue as string}
      onChange={(e) => setDraftValue(e.target.value)}
      inputProps={{ 'data-testid': `edit-${fieldKey}` }}
      sx={{ width: 200 }}
    />
  );

  const fieldRows: Array<{
    label: string;
    field: EditableField;
    display: string | JSX.Element;
    editControl: JSX.Element;
    infoText?: string;
  }> = [
    {
      label: 'First Name',
      field: 'first_name',
      display: profile?.first_name || '—',
      editControl: textEditControl('first_name'),
    },
    {
      label: 'Middle Name',
      field: 'middle_name',
      display: profile?.middle_name || '—',
      editControl: textEditControl('middle_name'),
    },
    {
      label: 'Last Name',
      field: 'last_name',
      display: profile?.last_name || '—',
      editControl: textEditControl('last_name'),
    },
    {
      label: 'Date of Birth',
      field: 'date_of_birth',
      display: formatDob(profile?.date_of_birth ?? null),
      editControl: textEditControl('date_of_birth', 'date'),
    },
    {
      label: 'SSN',
      field: 'ssn',
      display: ssnDisplay(),
      editControl: ssnEditControl(),
    },
    {
      label: 'Gender',
      field: 'gender',
      display: GENDER_LABELS[profile?.gender ?? ''] ?? '—',
      editControl: selectEditControl(GENDER_OPTIONS),
    },
    {
      label: 'Disability Status',
      field: 'disability_status',
      display: DISABILITY_LABELS[profile?.disability_status ?? ''] ?? '—',
      editControl: selectEditControl(DISABILITY_OPTIONS),
      infoText:
        'Some units have built-in accommodations (e.g. wheelchair access, visual/auditory alerts) reserved for residents with specific disabilities—sharing yours helps us match you to eligible units.',
    },
    ...(profile?.disability_status === 'yes'
      ? [
          {
            label: 'Disability Types',
            field: 'disability_types' as EditableField,
            display:
              (profile?.disability_types ?? []).length > 0
                ? (profile.disability_types ?? [])
                    .map((v) => DISABILITY_TYPE_LABELS[v] ?? v)
                    .join(', ')
                : '—',
            editControl: disabilityTypesEditControl(),
          },
        ]
      : []),
    {
      label: 'Ethnicity',
      field: 'ethnicity',
      display: ETHNICITY_LABELS[profile?.ethnicity ?? ''] ?? '—',
      editControl: selectEditControl(ETHNICITY_OPTIONS),
    },
    {
      label: 'Race',
      field: 'race_ethnicity',
      display: RACE_LABELS[profile?.race_ethnicity?.[0] ?? ''] ?? '—',
      editControl: selectEditControl(RACE_OPTIONS),
    },
    ...(profile?.race_ethnicity?.includes('asian')
      ? [
          {
            label: 'Asian Heritage',
            field: 'asian_sub' as EditableField,
            display: ASIAN_SUB_LABELS[profile.asian_sub?.[0] ?? ''] ?? '—',
            editControl: selectEditControl(ASIAN_SUB_OPTIONS),
          },
        ]
      : []),
    ...(profile?.race_ethnicity?.includes('native_hawaiian_pacific_islander')
      ? [
          {
            label: 'Pacific Islander Heritage',
            field: 'pacific_islander_sub' as EditableField,
            display:
              PACIFIC_ISLANDER_SUB_LABELS[
                profile.pacific_islander_sub?.[0] ?? ''
              ] ?? '—',
            editControl: selectEditControl(PACIFIC_ISLANDER_SUB_OPTIONS),
          },
        ]
      : []),
    {
      label: 'Citizenship',
      field: 'citizenship_status',
      display: CITIZENSHIP_LABELS[profile?.citizenship_status ?? ''] ?? '—',
      editControl: selectEditControl(CITIZENSHIP_OPTIONS),
    },
  ];

  return (
    <Box sx={{ backgroundColor: hubPageBg, minHeight: '100%' }}>
      <PageHeader
        title="Personal Information"
        icon={<PersonIcon />}
        backRoute={backRoute}
        subtitle="Your demographic and personal details"
      />
      <ProxyBanner />

      <Stack gap={2} sx={{ p: 3 }}>
        <Box
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: '16px',
            boxShadow: '0px 2px 10px rgba(0,0,0,0.063)',
            overflow: 'hidden',
          }}
        >
          {fieldRows.map((row, i) => (
            <Box key={row.field}>
              {renderFieldRow(
                row.label,
                row.field,
                row.display,
                row.editControl,
                row.infoText
              )}
              {i < fieldRows.length - 1 && <Divider />}
            </Box>
          ))}
        </Box>

        <Typography
          sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center' }}
        >
          Changes apply to new certifications; in-progress ones keep their
          snapshot.
        </Typography>
      </Stack>

      {verifyOpen && (
        <VerifyCodeModal
          purpose="elevated_auth"
          totpEnabled={elevatedAuthState.totp_enabled}
          onSuccess={handleElevationSuccess}
        />
      )}
    </Box>
  );
};

export default PersonalInfoPage;
